import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { BudgetRevision } from '@/types/budget.types';
import { formatCurrency, formatPercent } from '@/lib/budget-utils';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';

interface BudgetComparisonViewProps {
  revision1: BudgetRevision;
  revision2: BudgetRevision;
}

export function BudgetComparisonView({
  revision1,
  revision2,
}: BudgetComparisonViewProps) {
  const getChangeIndicator = (value: number) => {
    if (value > 0) {
      return (
        <span className="flex items-center gap-1 text-error-600">
          <ArrowUp className="h-4 w-4" />
          +{formatCurrency(value)}
        </span>
      );
    } else if (value < 0) {
      return (
        <span className="flex items-center gap-1 text-success-600">
          <ArrowDown className="h-4 w-4" />
          {formatCurrency(value)}
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-muted-foreground">
        <Minus className="h-4 w-4" />
        No change
      </span>
    );
  };

  const getPercentageChange = (oldValue: number, newValue: number) => {
    if (oldValue === 0) return newValue > 0 ? 100 : 0;
    return ((newValue - oldValue) / oldValue) * 100;
  };

  // Extract data from revisions
  const rev1Data = revision1.budgetData;
  const rev2Data = revision2.budgetData;

  // Calculate changes
  const costChange = (rev2Data?.totalCost || 0) - (rev1Data?.totalCost || 0);
  const revenueChange = (rev2Data?.totalRevenue || 0) - (rev1Data?.totalRevenue || 0);
  const marginChange = (rev2Data?.margin || 0) - (rev1Data?.margin || 0);

  return (
    <div className="space-y-6">
      {/* Revision Info */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{revision1.revisionNumber}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Date:</span>{' '}
              {revision1.createdAt ? new Date(revision1.createdAt).toLocaleDateString('en-GB') : new Date(revision1.date).toLocaleDateString('en-GB')}
            </div>
            <div>
              <span className="text-muted-foreground">By:</span> {revision1.createdBy || revision1.changedByName}
            </div>
            {revision1.reason && (
              <div>
                <span className="text-muted-foreground">Reason:</span> {revision1.reason}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{revision2.revisionNumber}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Date:</span>{' '}
              {revision2.createdAt ? new Date(revision2.createdAt).toLocaleDateString('en-GB') : new Date(revision2.date).toLocaleDateString('en-GB')}
            </div>
            <div>
              <span className="text-muted-foreground">By:</span> {revision2.createdBy || revision2.changedByName}
            </div>
            {revision2.reason && (
              <div>
                <span className="text-muted-foreground">Reason:</span> {revision2.reason}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Summary Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">{revision1.revisionNumber}</TableHead>
                <TableHead className="text-right">{revision2.revisionNumber}</TableHead>
                <TableHead className="text-right">Change</TableHead>
                <TableHead className="text-right">% Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Total Cost</TableCell>
                <TableCell className="text-right">
                  {formatCurrency(rev1Data?.totalCost || 0)}
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(rev2Data?.totalCost || 0)}
                </TableCell>
                <TableCell className="text-right">
                  {getChangeIndicator(costChange)}
                </TableCell>
                <TableCell className="text-right">
                  {formatPercent(getPercentageChange(rev1Data?.totalCost || 0, rev2Data?.totalCost || 0))}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="font-medium">Total Revenue</TableCell>
                <TableCell className="text-right">
                  {formatCurrency(rev1Data?.totalRevenue || 0)}
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(rev2Data?.totalRevenue || 0)}
                </TableCell>
                <TableCell className="text-right">
                  {getChangeIndicator(revenueChange)}
                </TableCell>
                <TableCell className="text-right">
                  {formatPercent(getPercentageChange(rev1Data?.totalRevenue || 0, rev2Data?.totalRevenue || 0))}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="font-medium">Margin</TableCell>
                <TableCell className="text-right">
                  {formatPercent(rev1Data?.margin || 0)}
                </TableCell>
                <TableCell className="text-right">
                  {formatPercent(rev2Data?.margin || 0)}
                </TableCell>
                <TableCell className="text-right">
                  {marginChange > 0 ? (
                    <span className="text-success-600">+{formatPercent(marginChange)}</span>
                  ) : marginChange < 0 ? (
                    <span className="text-error-600">{formatPercent(marginChange)}</span>
                  ) : (
                    <span className="text-muted-foreground">No change</span>
                  )}
                </TableCell>
                <TableCell className="text-right">-</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Cost Category Changes */}
      {rev1Data?.costCategories && rev2Data?.costCategories && (
        <Card>
          <CardHeader>
            <CardTitle>Cost Category Changes</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">{revision1.revisionNumber}</TableHead>
                  <TableHead className="text-right">{revision2.revisionNumber}</TableHead>
                  <TableHead className="text-right">Change</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.keys({ ...rev1Data.costCategories, ...rev2Data.costCategories }).map((category) => {
                  const oldValue = rev1Data.costCategories?.[category] || 0;
                  const newValue = rev2Data.costCategories?.[category] || 0;
                  const change = newValue - oldValue;

                  if (change === 0 && oldValue === 0 && newValue === 0) return null;

                  return (
                    <TableRow key={category}>
                      <TableCell className="font-medium capitalize">
                        {category.replace(/([A-Z])/g, ' $1').trim()}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(oldValue)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(newValue)}
                      </TableCell>
                      <TableCell className="text-right">
                        {getChangeIndicator(change)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Change Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Change Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {costChange !== 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                <span className="font-medium">Total Cost Change</span>
                <div className="flex items-center gap-2">
                  {getChangeIndicator(costChange)}
                  <Badge variant={costChange > 0 ? 'destructive' : 'default'}>
                    {formatPercent(getPercentageChange(rev1Data?.totalCost || 0, rev2Data?.totalCost || 0))}
                  </Badge>
                </div>
              </div>
            )}

            {revenueChange !== 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                <span className="font-medium">Total Revenue Change</span>
                <div className="flex items-center gap-2">
                  {getChangeIndicator(revenueChange)}
                  <Badge variant={revenueChange > 0 ? 'default' : 'destructive'}>
                    {formatPercent(getPercentageChange(rev1Data?.totalRevenue || 0, rev2Data?.totalRevenue || 0))}
                  </Badge>
                </div>
              </div>
            )}

            {marginChange !== 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                <span className="font-medium">Margin Change</span>
                <div className="flex items-center gap-2">
                  <span className={marginChange > 0 ? 'text-success-600' : 'text-error-600'}>
                    {marginChange > 0 ? '+' : ''}{formatPercent(marginChange)}
                  </span>
                </div>
              </div>
            )}

            {costChange === 0 && revenueChange === 0 && marginChange === 0 && (
              <div className="text-center text-muted-foreground py-4">
                No significant changes between these revisions
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
